import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, ArrowLeft, Loader2, MailCheck } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-am-bg px-5 py-10">
      <div className="pointer-events-none absolute left-1/2 top-[-10%] h-[460px] w-[460px] -translate-x-1/2 aura-radial opacity-[0.16] blur-3xl" />
      <div className="relative w-full max-w-[400px]">
        <button onClick={() => navigate("/login")} className="mx-auto mb-6 flex flex-col items-center gap-3">
          <Logo size={60} />
          <span className="font-mark text-xl font-extrabold tracking-tight text-white">AURAMAX</span>
        </button>

        <div className="rounded-3xl border border-am-border bg-am-surface/60 p-6 backdrop-blur-xl sm:p-7">
          {sent ? (
            <div className="text-center" data-testid="forgot-sent">
              <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl aura-gradient"><MailCheck className="h-7 w-7 text-black" /></div>
              <h1 className="font-display text-2xl font-extrabold text-white">Check your email</h1>
              <p className="mt-2 text-sm leading-relaxed text-am-text2">
                If an account exists for <span className="text-white">{email}</span>, we've sent a link to reset your password. It expires in 1 hour.
              </p>
              <button onClick={() => navigate("/login")} className="mt-6 w-full rounded-full bg-white py-3 text-sm font-bold text-black">Back to log in</button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-extrabold text-white">Reset your password</h1>
              <p className="mt-1 text-sm text-am-text2">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={submit} className="mt-6 space-y-3">
                <div className="flex items-center rounded-xl border border-am-border bg-am-surface2 px-3 focus-within:border-am-border-strong">
                  <Mail className="h-4 w-4 text-am-text3" />
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="forgot-email" placeholder="Email" className="w-full bg-transparent px-3 py-3 text-sm text-white placeholder:text-am-text3 outline-none" />
                </div>
                {error && <p data-testid="forgot-error" className="rounded-lg bg-aura-magenta/10 px-3 py-2 text-xs text-aura-magenta">{error}</p>}
                <button type="submit" disabled={busy} data-testid="forgot-submit" className="flex w-full items-center justify-center gap-2 rounded-full p-[1.5px] aura-gradient disabled:opacity-60">
                  <span className="flex w-full items-center justify-center gap-2 rounded-full bg-am-bg py-3 text-sm font-bold text-white">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
                  </span>
                </button>
              </form>
              <button onClick={() => navigate("/login")} className="mt-5 flex w-full items-center justify-center gap-1.5 text-sm text-am-text2 hover:text-white">
                <ArrowLeft className="h-4 w-4" /> Back to log in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
